import { Box, Grid } from '@mui/material'
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Fade } from 'utoniq-ui'

import { EmphasisRarityId, RewardItemWithFistTimeObtainedRare } from '.'
import { AnimationFrame } from './AnimationFrame'
import { ItemWithRarityCell } from './ItemWithRarityCell'
import { LightFrame } from './LightFrame'
import { RareItemModal } from './RareItemModal'

type AnimationProps = {
  emphasis: EmphasisRarityId[]
  startFlip: boolean
  allItemAppeared: boolean
  skipAnimation: boolean
  fistTimeObtainedRareEffect?: boolean
  setAllItemAppeared: (appeared: boolean) => void
  setAnimationComplete: Dispatch<SetStateAction<boolean[]>>
  setCompleteAllAnimation: Dispatch<SetStateAction<boolean>>
}
type AnimationTimes = {
  appearItem: number
  slide: number
  allItemAppeared: number
  flip: {
    fistTimeObtainedRare: number
    common: number
  }
  growLight: {
    common: number
    rare: number
  }
  animationCompleted: number
}
type ItemCellProps = {
  rewardItem: RewardItemWithFistTimeObtainedRare
  rewardItemOrder: number
  onClick: () => void
  animation: AnimationProps
}
/**
 * @description
 * アイテム表示コンポーネント
 */
export const ItemCell: FC<ItemCellProps> = ({
  rewardItem,
  rewardItemOrder,
  onClick,
  animation,
}) => {
  const { item } = rewardItem

  const ref = useRef<HTMLDivElement>(null)

  /**
   * アニメーションの時間設定(ms)
   */
  const animationTimes: AnimationTimes = {
    appearItem: 100, // アイテム出現の時間間隔
    slide: 1000, // アイテム出現スライドの時間
    allItemAppeared: 2000, // 全アイテム出現後の隙間時間
    // アイテムサムネイル反転アニメーションの時間
    flip: {
      common: 1000, // 通常パターン
      fistTimeObtainedRare: 6000, // 初回レア取得パターン
    },
    // アイテムが光る演出時間
    growLight: {
      common: 1000, // 通常パターン
      rare: 5000, // 初回レア取得パターン
    },
    animationCompleted: 1000, // 全アイテム出現後の空白時間
  }

  const { appear, flipped, openRareItemModal, onItemAppeared } =
    useGachaResultAnimation(animation, animationTimes, rewardItemOrder)

  return (
    <Grid item justifyContent="center" ref={ref} sx={{ overflow: 'hidden' }}>
      {animation.skipAnimation ? (
        <Fade>
          <Box>
            <LightFrame
              emphasisRarityId={item?.rarity?.id as EmphasisRarityId}
            />
            <ItemWithRarityCell item={item} onClick={onClick} flipped={true} />
          </Box>
        </Fade>
      ) : (
        <AnimationFrame
          appear={appear}
          emphasisRarityId={item?.rarity?.id as EmphasisRarityId}
          rarityEffect={animation.fistTimeObtainedRareEffect}
          flipped={flipped}
          slide={animationTimes.slide}
          growLightTimes={animationTimes.growLight}
          container={ref.current}
          addEndListener={onItemAppeared}
        >
          <ItemWithRarityCell item={item} onClick={onClick} flipped={flipped} />
        </AnimationFrame>
      )}

      <RareItemModal rewardItem={rewardItem} open={openRareItemModal} />
    </Grid>
  )
}

/**
 * ガチャ結果アニメーション用hook
 */
const useGachaResultAnimation = (
  animation: AnimationProps,
  animationTimes: AnimationTimes,
  rewardItemOrder: number
) => {
  const [appear, setAppear] = useState(false)
  const [flipped, setFlipped] = useState(false)
  const [openRareItemModal, setOpenRareItemModal] = useState(false)

  /**
   * 最初にslide + opacityでのフェードインでアイテム表示
   * 各アイテム0.1秒毎にアニメーション
   */
  useEffect(() => {
    setTimeout(
      () => setAppear(true),
      (rewardItemOrder + 1) * animationTimes.appearItem
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * アイテム出現完了時のイベント
   * 最後のアイテム出現完了の2秒後、出現アニメーション完了フラグを立てる
   */
  const onItemAppeared = () => {
    if (rewardItemOrder < 9) return

    setTimeout(
      () => animation.setAllItemAppeared(true),
      animationTimes.allItemAppeared
    )
    return
  }

  /**
   * アイテム全て表示後のアニメーションをコントロールする
   *
   * レア（初回取得）： レア用発光 ＋ 特殊表現 6秒
   * レア（取得済み) or ノーマル： ノーマル用発光 1秒
   */
  useEffect(() => {
    if (!animation.allItemAppeared) return

    if (!animation.startFlip) return

    setFlipped(true)

    animation.fistTimeObtainedRareEffect &&
      setTimeout(() => {
        setOpenRareItemModal(true)
        setTimeout(
          () => setOpenRareItemModal(false),
          animationTimes.flip.fistTimeObtainedRare / 2
        )
      }, animationTimes.growLight.rare / 2)

    setTimeout(
      () => {
        // 現在のアイテムのアニメーションステータス更新
        animation.setAnimationComplete(statuses => {
          return statuses.map((status, index) => {
            if (rewardItemOrder !== index) return status

            return true
          })
        })

        // 最後のアイテムの場合、1秒後にアニメーション完了フラグを立てる
        if (rewardItemOrder !== 9) return

        setTimeout(
          () => animation.setCompleteAllAnimation(true),
          animationTimes.animationCompleted
        )
      },
      animation.fistTimeObtainedRareEffect
        ? animationTimes.flip.fistTimeObtainedRare
        : animationTimes.flip.common
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    animation.allItemAppeared,
    animation.startFlip,
    animation.fistTimeObtainedRareEffect,
  ])

  return {
    appear,
    flipped,
    openRareItemModal,
    onItemAppeared,
  }
}
